import React, {useState} from 'react';
import {Box} from 'src/design/atoms/Box';
import {Flex} from 'src/design/atoms/Flex';
import {Group} from 'src/design/atoms/Group';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconTrash} from '@tabler/icons-react';
import type {ProjectAssignmentRuleResponse} from 'src/api/ProactApiClient';
import {ActionIcon} from 'src/design/atoms/ActionIcon';
import {Button} from 'src/design/atoms/Button';
import {Pill} from 'src/design/atoms/Pill';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {
  describeAudienceFacts,
  pluralizePeople,
  selectAllAudienceLabel,
  type SourceEntityNameLookup,
} from 'src/app/assignment-rules/assignmentRuleHelpers';
import {useDeleteProjectAssignmentRuleMutation} from 'src/redux/queries/projectAssignmentRule';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface AssignmentRuleCardProps {
  rule: ProjectAssignmentRuleResponse;
  nameLookup: SourceEntityNameLookup;
  canEdit: boolean;
  onEdit: () => void;
}

export const AssignmentRuleCard: React.FC<AssignmentRuleCardProps> = ({
  rule,
  nameLookup,
  canEdit,
  onEdit,
}) => {
  const {setAlert} = useAlert();
  const [confirmDeleteOpened, setConfirmDeleteOpened] = useState(false);

  const [deleteRule, {isLoading: isDeleting}] =
    useDeleteProjectAssignmentRuleMutation();

  const handleDelete = async () => {
    try {
      await deleteRule(rule.id).unwrap();
      setAlert('Assignment rule deleted.', 'success');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    } finally {
      setConfirmDeleteOpened(false);
    }
  };

  const isSelectAll = rule.selectAllEntityType != null;
  const audienceFacts = isSelectAll
    ? []
    : describeAudienceFacts(rule.sourceEntities ?? [], nameLookup);

  return (
    <Paper
      withBorder
      shadow="xs"
      p="md"
      data-testid={`assignment-rule-card-${rule.id}`}>
      <Flex justify="space-between" align="flex-start" gap="sm">
        <Box style={{flex: 1}}>
          {rule.name && (
            <Text fw={600} mb={4}>
              {rule.name}
            </Text>
          )}
          <Stack gap={2}>
            <Group gap={4}>
              <Text size="xs" fw={600} c="dimmed">
                Audience:
              </Text>
              {isSelectAll ? (
                <Pill
                  text={selectAllAudienceLabel(rule.selectAllEntityType!)}
                  color="gray"
                />
              ) : audienceFacts.length === 0 ? (
                <Text size="xs" c="dimmed">
                  No one selected
                </Text>
              ) : (
                audienceFacts.map(fact => (
                  <Pill
                    key={fact.entityType}
                    text={`${fact.label}: ${fact.value}`}
                    color="gray"
                  />
                ))
              )}
            </Group>
            <Group gap={4}>
              <Text size="xs" fw={600} c="dimmed">
                Resolves to:
              </Text>
              <Text size="xs">{pluralizePeople(rule.resolvedCount ?? 0)}</Text>
            </Group>
          </Stack>
          {!rule.active && (
            <Box mt={4}>
              <Pill text="Inactive" color="yellow" />
            </Box>
          )}
        </Box>
        <Group gap="xs" wrap="nowrap">
          {canEdit && (
            <>
              <Button size="xs" variant="subtle" onClick={onEdit}>
                Edit
              </Button>
              <ActionIcon
                tooltipText="Delete rule"
                color="red"
                onClick={() => setConfirmDeleteOpened(true)}>
                <IconTrash size={16} />
              </ActionIcon>
            </>
          )}
        </Group>
      </Flex>

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => setConfirmDeleteOpened(false)}
        onConfirm={handleDelete}
        title="Delete this rule?"
        body="People assigned only through this rule will be un-assigned. This cannot be undone."
        confirmationButtonColor="red"
        confirmationButtonDisabled={isDeleting}
      />
    </Paper>
  );
};
